/* eslint-disable camelcase */
import { ISignUpResult, CognitoUser } from 'amazon-cognito-identity-js'
import { Auth } from 'aws-amplify'
import { path } from 'ramda'
import {
  UpdateUserAttributes,
  UserInfo,
  UserSession,
  UserInfoFromSession,
  SignupProps
} from './types'
import { updateLastUsedFrontend } from '../users'

const getSession = async (): Promise<UserSession | undefined> => {
  try {
    const session: UserSession = await Auth.currentSession()
    return session
  } catch (error) {
    return undefined
  }
}

const getSubFromSession = (session: UserSession): string | undefined =>
  path(['accessToken', 'payload', 'sub'], session)

const getRoleFromSession = (session: UserSession): string => {
  const groups: string[] =
    path(['accessToken', 'payload', 'cognito:groups'], session) || []
  return groups.includes('admin') ? 'admin' : 'user'
}

const getCurrentUserInfo = async (): Promise<UserInfo> => {
  const userInfo = await Auth.currentUserInfo()
  return userInfo
}

const getUserInfoFromSession = async (): Promise<UserInfoFromSession> => {
  const session: UserSession | undefined = await getSession()
  if (session) {
    const sub = getSubFromSession(session)
    if (sub) {
      const role = getRoleFromSession(session)
      const { attributes } = await getCurrentUserInfo()
      return {
        sub,
        role,
        attributes
      }
    }
  }
  throw new Error('Invalid user session')
}

const getCurrentCredentials = async () => {
  const credentials = await Auth.currentCredentials()
  return credentials
}

const authLogin = async (email: string, password: string) => {
  const cognitoUser: CognitoUser = await Auth.signIn(email, password)
  return cognitoUser
}

const authResendActivationEmail = async (email: string): Promise<void> => {
  await updateLastUsedFrontend(email)
  await Auth.resendSignUp(email)
}

const authLogout = async () => {
  await Auth.signOut()
}

const authSignup = async ({
  email,
  password,
  name,
  phone_number,
  'custom:language': language,
  isSVAMember
}: SignupProps) => {
  const signupSchema = {
    username: email,
    email,
    password,
    attributes: {
      /** don't add name key if undefined */
      ...(name && { name: name }),
      phone_number,
      'custom:language': language,
      'custom:frontend': 'fiksuvuokraus'
    },
    ...(isSVAMember && { isSVAMember: isSVAMember }),
    emailUnconfirmed: true
  }
  const signupResult: ISignUpResult = await Auth.signUp(signupSchema)
  return signupResult
}

const authUpdateUserInfo = async (userAttributes: UpdateUserAttributes) => {
  const user = await Auth.currentAuthenticatedUser()
  const updatedUser = await Auth.updateUserAttributes(user, userAttributes)
  return updatedUser
}

const authRenewPassword = async (
  cognitoUser: any,
  password: string,
  requiredAttributes: any
) => {
  await Auth.completeNewPassword(cognitoUser, password, requiredAttributes)
}

const authChangePassword = async (
  currentPassword: string,
  password: string
) => {
  const user = await Auth.currentAuthenticatedUser()
  await Auth.changePassword(user, currentPassword, password)
}

const authForgotPassword = async (email: string) => {
  await updateLastUsedFrontend(email)
  await Auth.forgotPassword(email)
}

const authForgotPasswordSubmit = async (
  email: string,
  code: string,
  password: string
) => {
  await Auth.forgotPasswordSubmit(email, code, password)
}

const authConfirmSignUp = async (email: string, code: string) => {
  await updateLastUsedFrontend(email)
  await Auth.confirmSignUp(email, code)
}

const authResendSignUpVerificationCode = async (email: string) => {
  await updateLastUsedFrontend(email)
  await Auth.resendSignUp(email)
}

const authVerifyEmail = async () => {
  await Auth.verifyCurrentUserAttribute('email')
}

const authVerifyEmailSubmit = async (code: string) => {
  await Auth.verifyCurrentUserAttributeSubmit('email', code)
}

const authLoginErrorCodes = (errorCode: string) => {
  switch (errorCode) {
    case 'UserNotConfirmedException':
      return `login.${errorCode}`
    case 'NotAuthorizedException':
      return `login.${errorCode}`
    case 'UserNotFoundException':
      return `login.${errorCode}`
    default:
      return 'login.error'
  }
}

export {
  getSession,
  getUserInfoFromSession,
  getCurrentUserInfo,
  getCurrentCredentials,
  authLogin,
  authResendActivationEmail,
  authLogout,
  authSignup,
  authUpdateUserInfo,
  authRenewPassword,
  authChangePassword,
  authForgotPassword,
  authForgotPasswordSubmit,
  authVerifyEmail,
  authVerifyEmailSubmit,
  authConfirmSignUp,
  authResendSignUpVerificationCode,
  authLoginErrorCodes
}
